import React, { useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import {
  Button as AntdButton,
  Checkbox as AntdCheckbox,
  Dropdown as AntdDropdown,
  message as AntdMessage,
  Modal as AntdModal,
} from 'antd';

import { UserDto } from 'lib_api/lib/api/gen';

import {
  ColumnProps,
  TableHeaderFunctionWithoutSort,
} from 'components/WrappedComponents/Table/types';
import { useComptesUtilisateursFilterContext } from 'hooks/FilterComptesUtilisateursStoreContext';
import { usePaginationContext } from 'hooks/PaginationStoreContext';
import { profileEnumMapping } from 'utils/enumData';

import TableWithoutSort from '../../../WrappedComponents/Table/TableWithoutSort';
import { useSubmitDeleteAccount } from '../../DeleteAccount/utils';
import ModalModification from '../../Modification/ModalModification';
import { useSubmitResetUserPassword } from '../../ResetPassword/utils';
import { LONG_DELAY } from '../constants';
import DashboardHeader from '../DashboardHeader';
import { FilterDashboardComptesUtilisateurs } from '../DashboardHeader/types';

import { useSearchUserDto } from './useSearchUserDto';

import './DashboardTable.less';

function buildEnabledContent(data: UserDto): React.ReactElement {
  return <AntdCheckbox checked={data.enabled ?? undefined} disabled={true} />;
}

function buildGestionnaireLocalContent(
  data: UserDto,
): React.ReactElement | null {
  if (data.gestionnaireLocal === null) {
    return null;
  }
  switch (data.profile) {
    case 'FORCE_DE_L_ORDRE':
    case 'GARDIEN_FOURRIERE':
    case 'AUTORITE_FOURRIERE':
      return <AntdCheckbox checked={data.gestionnaireLocal} disabled={true} />;
    default:
      return null;
  }
}

function buildActionsContent(
  data: UserDto,
  openModalModification: (user: UserDto) => void,
  openModalResetPassword: (user: UserDto) => void,
  openModalDeleteAccount: (user: UserDto) => void,
): React.ReactElement {
  const items = [
    {
      key: '1',
      label: (
        <span
          onClick={() => {
            openModalResetPassword(data);
          }}
        >
          Réinitialiser le mot de passe
        </span>
      ),
    },
    {
      key: '2',
      label: (
        <span
          onClick={() => {
            openModalModification(data);
          }}
        >
          Modifier l&apos;utilisateur
        </span>
      ),
    },
    {
      key: '3',
      label: (
        <span
          onClick={() => {
            openModalDeleteAccount(data);
          }}
        >
          Supprimer l&apos;utilisateur
        </span>
      ),
    },
  ];
  return (
    <>
      <AntdDropdown menu={{ items }}>
        <AntdButton>
          Actions <DownOutlined />
        </AntdButton>
      </AntdDropdown>
    </>
  );
}

const buildHeader: TableHeaderFunctionWithoutSort<
  UserDto,
  FilterDashboardComptesUtilisateurs
> = (fetchedData, _selectedData, filters, setFilter) => {
  return (
    <DashboardHeader
      filter={filters}
      setFilter={setFilter}
      totalResult={fetchedData?.total || 0}
    />
  );
};

const DashBoardTable: React.FC = () => {
  const searchUser = useSearchUserDto();
  const [pagination, setPagination] = usePaginationContext(
    'paginationAdministrationComptes',
  );
  const [filter, setFilter] = useComptesUtilisateursFilterContext();
  const [updatedUser, setUpdatedUser] = useState<UserDto>();
  const [isLoadingResetPassword, setIsLoadingResetPassword] =
    useState<boolean>(false);
  const [isLoadingDeleteAccount, setIsLoadingDeleteAccount] =
    useState<boolean>(false);
  const [modal, contextHolder] = AntdModal.useModal();

  const closeModalModification = (): void => {
    setUpdatedUser(undefined);
  };

  const submitResetPassword = useSubmitResetUserPassword(
    setIsLoadingResetPassword,
  );

  const submitDeleteAccount = useSubmitDeleteAccount(setIsLoadingDeleteAccount);

  const openModalConfirmResetPassword = (user: UserDto) => {
    if (user.email) {
      modal.confirm({
        title: 'Voulez-vous vraiment réinitialiser le mot de passe ?',
        okText: 'Réinitialiser',
        cancelText: 'Annuler',
        onOk: () => {
          submitResetPassword(user.id, user.username, user.email);
        },
        okButtonProps: {
          loading: isLoadingResetPassword,
        },
      });
    } else {
      void AntdMessage.error(
        `Veuillez configurer l'adresse mail du compte ${user.username} pour pouvoir réinitialiser son mot de passe.`,
        LONG_DELAY,
      );
    }
  };

  const openModalConfirmDeleteAccount = (user: UserDto) => {
    modal.confirm({
      title: `Voulez-vous vraiment supprimer l'utilisateur ${user.username} ?`,
      okText: 'Supprimer',
      cancelText: 'Annuler',
      onOk: () => {
        submitDeleteAccount(user.id, user.username);
      },
      okButtonProps: {
        loading: isLoadingDeleteAccount,
      },
    });
  };

  function buildColumns(): ColumnProps<UserDto>[] {
    return [
      {
        title: 'Nom',
        buildCellContent: data => data.lastName,
      },
      {
        title: 'Prénom',
        buildCellContent: data => data.firstName,
      },
      {
        title: "Nom d'utilisateur",
        buildCellContent: data => data.username,
      },
      {
        title: 'Profil',
        buildCellContent: data => profileEnumMapping[data.profile].name,
      },
      {
        title: 'Adresse e-mail',
        buildCellContent: data => data.email,
      },
      {
        title: 'Entité de rattachement',
        buildCellContent: data => data.libelleEntiteRattachement,
      },
      {
        title: 'Gestionnaire local',
        buildCellContent: data => buildGestionnaireLocalContent(data),
      },
      {
        title: 'Actif',
        buildCellContent: data => buildEnabledContent(data),
      },
      {
        title: '',
        buildCellContent: data =>
          buildActionsContent(
            data,
            setUpdatedUser,
            openModalConfirmResetPassword,
            openModalConfirmDeleteAccount,
          ),
      },
    ];
  }

  const columns: ColumnProps<UserDto>[] = buildColumns();

  return (
    <div className="table-container">
      <TableWithoutSort
        columns={columns}
        fetchData={searchUser}
        header={buildHeader}
        buildItemKey={(item): string => item.id}
        pageSizeOptions={[10, 20, 50]}
        pagination={pagination}
        setPagination={setPagination}
        filter={filter}
        setFilter={setFilter}
      />
      {updatedUser !== undefined && (
        <ModalModification
          close={closeModalModification}
          userToUpdate={updatedUser}
          callAfterSubmit={() => {
            setFilter({ ...filter });
          }}
        />
      )}
      {contextHolder}
    </div>
  );
};

export default DashBoardTable;
